import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Icono } from "../Icono";
import type { DataTableRowAction } from "./types";

/**
 * Menú de acciones de una fila.
 *
 * Un menú y no una hilera de botones: con cinco acciones por fila —y Cuentas
 * tiene ocho— la tabla se convierte en una pared de botones donde lo
 * importante, que son los datos, deja de verse. Y en móvil no caben.
 *
 * # Por qué el menú vive fuera de la tabla
 *
 * La tabla se desplaza en horizontal, así que su contenedor lleva
 * `overflow-x: auto`. Y ahí está la trampa: en CSS, cuando un eje deja de ser
 * `visible`, el otro **también** se vuelve `auto`. Un menú posicionado dentro
 * de ese contenedor se recortaba por abajo y, de paso, hacía aparecer una
 * barra de desplazamiento vertical en una tabla de dos filas.
 *
 * Por eso se pinta en `document.body` con posición fija, calculada a partir
 * del botón que lo abre. Se cierra al desplazar la página en lugar de
 * perseguir el botón: un menú que flota lejos de su fila es peor que uno que
 * se cierra.
 *
 * # Teclado
 *
 * Abre con Enter o Espacio, recorre con las flechas, cierra con Escape
 * devolviendo el foco al disparador. Sin eso, quien navega con teclado llega
 * al menú y se queda atrapado dentro de la tabla.
 */
export function DataTableRowActions<TData>({
  row,
  actions,
}: {
  row: TData;
  actions: DataTableRowAction<TData>[];
}) {
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const disparador = useRef<HTMLButtonElement>(null);
  const id = useId();

  const visibles = actions.filter((a) => !a.hidden?.(row));

  // Ordenadas con las destructivas al final: para pulsar «Borrar datos» hay
  // que pasar por delante de las demás, y eso es justo lo que se quiere.
  const ordenadas = [
    ...visibles.filter((a) => a.tone !== "danger"),
    ...visibles.filter((a) => a.tone === "danger"),
  ];

  // Antes de pintar, para que nadie vea el menú en la esquina antes de que
  // salte a su sitio.
  useLayoutEffect(() => {
    if (!abierto) {
      setPosicion(null);
      return;
    }
    const ancla = disparador.current?.getBoundingClientRect();
    const caja = menu.current?.getBoundingClientRect();
    if (!ancla || !caja) return;

    const margen = 8;
    // Alineado por la derecha con el botón, que es donde está el menú; si no
    // cabe, se mete hacia dentro en lugar de salirse de la ventana.
    const left = Math.max(
      margen,
      Math.min(ancla.right - caja.width, window.innerWidth - caja.width - margen),
    );
    // Hacia abajo salvo que no quepa: entonces por encima del botón.
    const debajo = ancla.bottom + 4;
    const top =
      debajo + caja.height > window.innerHeight - margen
        ? Math.max(margen, ancla.top - caja.height - 4)
        : debajo;

    setPosicion({ top, left });
  }, [abierto, ordenadas.length]);

  useEffect(() => {
    if (!abierto) return;

    function fuera(event: MouseEvent) {
      const destino = event.target as Node;
      if (contenedor.current?.contains(destino)) return;
      if (menu.current?.contains(destino)) return;
      setAbierto(false);
    }
    // El menú está anclado a una posición de pantalla, no a la fila: si lo que
    // hay debajo se mueve, se cierra.
    function mover() {
      setAbierto(false);
    }

    document.addEventListener("mousedown", fuera);
    window.addEventListener("scroll", mover, true);
    window.addEventListener("resize", mover);
    return () => {
      document.removeEventListener("mousedown", fuera);
      window.removeEventListener("scroll", mover, true);
      window.removeEventListener("resize", mover);
    };
  }, [abierto]);

  if (ordenadas.length === 0) return null;

  function cerrar() {
    setAbierto(false);
    disparador.current?.focus();
  }

  function enTeclado(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      cerrar();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const paso = event.key === "ArrowDown" ? 1 : -1;
      setActivo((previo) => (previo + paso + ordenadas.length) % ordenadas.length);
    }
  }

  return (
    <div ref={contenedor} className="relative inline-block text-left">
      <button
        ref={disparador}
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={abierto ? id : undefined}
        aria-label="Acciones de la fila"
        onClick={(e) => {
          e.stopPropagation();
          setAbierto((v) => !v);
          setActivo(0);
        }}
        onKeyDown={enTeclado}
        className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-slate-300 transition hover:bg-white/[0.09] hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400/60"
      >
        <Icono nombre="menu" className="h-4 w-4" />
      </button>

      {abierto &&
        createPortal(
          <div
            ref={menu}
            id={id}
            role="menu"
            onKeyDown={enTeclado}
            onClick={(e) => e.stopPropagation()}
            style={{
              top: posicion?.top ?? 0,
              left: posicion?.left ?? 0,
              // Mientras no se ha medido se deja transparente en lugar de
              // oculto: un elemento con `visibility: hidden` no recibe el foco,
              // y el primer elemento del menú lo toma al abrirse.
              opacity: posicion === null ? 0 : 1,
            }}
            className="fixed z-50 min-w-[12rem] overflow-hidden rounded-lg border border-white/10 bg-[#11162a] py-1 shadow-xl"
          >
            {ordenadas.map((accion, i) => {
              const motivo = accion.disabled?.(row);
              const deshabilitada = typeof motivo === "string";
              return (
                <button
                  key={accion.label}
                  role="menuitem"
                  type="button"
                  disabled={deshabilitada}
                  title={deshabilitada ? motivo : undefined}
                  autoFocus={i === activo}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAbierto(false);
                    accion.onSelect(row);
                  }}
                  onMouseEnter={() => setActivo(i)}
                  className={`block w-full px-3 py-1.5 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    accion.tone === "danger"
                      ? "text-rose-200 hover:bg-rose-500/15"
                      : "text-slate-200 hover:bg-white/[0.07]"
                  }`}
                >
                  {accion.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
